import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/translations";
import { resolveImg } from "@/lib/api";
import { Reveal } from "./Reveal";
import { Lightbox } from "./Lightbox";
import { Maximize2 } from "lucide-react";

const CATS = [
  { id: "tacos", num: "01", es: "Tacos", en: "Tacos", des_es: "Tortilla de maíz, cebolla, cilantro y salsa.", des_en: "Corn tortilla, onion, cilantro and salsa." },
  { id: "burritos", num: "02", es: "Burritos", en: "Burritos", des_es: "Enrollados con arroz, frijoles y queso.", des_en: "Rolled with rice, beans and cheese." },
  { id: "platos", num: "03", es: "Platos Fuertes", en: "Big Plates", des_es: "Porciones abundantes con sus acompañantes.", des_en: "Hearty portions with all the sides." },
  { id: "antojitos", num: "04", es: "Antojitos & Extras", en: "Snacks & Extras", des_es: "Para compartir, picar y disfrutar.", des_en: "To share, snack and enjoy." },
];

const ASPECTS = ["aspect-[3/4]", "aspect-square", "aspect-[4/5]", "aspect-[4/5]", "aspect-[3/4]", "aspect-square", "aspect-[4/5]"];

const CategoryBlock = ({ cat, items, lang, onZoom, reversed }) => {
  const list = items.filter((i) => i.category === cat.id).sort((a, b) => a.order - b.order);
  const seen = new Set();
  const photos = list.filter((i) => i.image && !seen.has(i.image) && seen.add(i.image));

  return (
    <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
      {/* PHOTOS */}
      {photos.length > 0 && (
        <div className={`lg:col-span-7 ${reversed ? "lg:order-2" : ""}`}>
          <div className={photos.length === 1 ? "" : "columns-2 gap-4"}>
            {photos.map((p, i) => (
              <motion.button
                key={p.image}
                data-testid={`cat-photo-${cat.id}-${i}`}
                onClick={() => onZoom({ src: resolveImg(p.image), caption: lang === "es" ? p.name_es : p.name_en })}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="group relative block w-full mb-4 break-inside-avoid overflow-hidden rounded-2xl border border-white/5 cursor-zoom-in"
              >
                <img
                  src={resolveImg(p.image)}
                  alt={lang === "es" ? p.name_es : p.name_en}
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${photos.length === 1 ? "aspect-[4/3]" : ASPECTS[i % ASPECTS.length]}`}
                />
                <span className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 size={14} />
                </span>
                <div className="absolute inset-x-0 bottom-0 p-4 pt-12 bg-gradient-to-t from-black/85 to-transparent">
                  <span className="font-accent text-lg text-[#A8E010] drop-shadow">{lang === "es" ? p.name_es : p.name_en}</span>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {/* LIST */}
      <div className={`${photos.length > 0 ? "lg:col-span-5" : "lg:col-span-12 max-w-3xl mx-auto"} ${reversed ? "lg:order-1" : ""}`}>
        <Reveal>
          <div className="relative mb-6">
            <span className="block font-display text-7xl md:text-8xl leading-none text-transparent [-webkit-text-stroke:1.5px_rgba(255,90,0,0.45)]">{cat.num}</span>
            <h3 className="font-display uppercase text-4xl md:text-5xl leading-[0.9] -mt-3">{lang === "es" ? cat.es : cat.en}</h3>
            <p className="text-sm text-neutral-400 mt-3 max-w-sm">{lang === "es" ? cat.des_es : cat.des_en}</p>
          </div>
        </Reveal>
        <ul className="divide-y divide-white/10">
          {list.map((it, i) => (
            <motion.li
              key={it.id}
              initial={{ opacity: 0, x: reversed ? -16 : 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.3) }}
              className="py-3.5"
            >
              <div className="flex items-baseline gap-3">
                <h4 className="font-display uppercase tracking-tight text-lg md:text-xl text-neutral-100">{lang === "es" ? it.name_es : it.name_en}</h4>
                <span className="flex-1 border-b border-dotted border-white/25 relative -top-1" />
                <span className="shrink-0 font-display text-lg md:text-xl text-[#FF5A00]">{it.price}</span>
              </div>
              {(lang === "es" ? it.desc_es : it.desc_en) && (
                <p className="text-xs md:text-sm text-neutral-500 mt-1 max-w-md leading-relaxed">{lang === "es" ? it.desc_es : it.desc_en}</p>
              )}
            </motion.li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const Menu = ({ items }) => {
  const { lang } = useLang();
  const [lb, setLb] = useState(null);

  return (
    <section id="menu" className="relative bg-[#0A0A0A] py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <span className="font-accent text-2xl text-[#FF5A00]">{t("menu_kicker", lang)}</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] mt-3 max-w-3xl">{t("menu_title", lang)}</h2>
        </Reveal>

        <div className="mt-16 md:mt-28 space-y-24 md:space-y-36">
          {CATS.map((cat, idx) => (
            <CategoryBlock key={cat.id} cat={cat} items={items} lang={lang} onZoom={setLb} reversed={idx % 2 === 1} />
          ))}
        </div>
      </div>
      <Lightbox open={!!lb} src={lb?.src} caption={lb?.caption} onClose={() => setLb(null)} />
    </section>
  );
};
